import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RestaurantPrinterService from '../../services/restaurant/RestaurantPrinterService';
import { AppDispatch, RootState } from '../../store/store';
import {
  closeRestaurantPrinterModal,
  openRestaurantPrinterCreate,
  openRestaurantPrinterEdit,
  setRestaurantPrinterLoading,
  setRestaurantPrinters,
  setRestaurantPrinterSaving,
  updateRestaurantPrinterField,
} from '../../store/slices/restaurant/restaurantPrinterSlice';
import {
  RestaurantPrinterDTO,
  RestaurantPrinterFilters,
  RestaurantPrinterForm,
} from '../../types/restaurant/RestaurantPrinterTypes';
import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';
import useConfirm from '../useConfirmModal';

const useRestaurantPrinters = (branchId: number | '') => {
  const dispatch = useDispatch<AppDispatch>();
  const state = useSelector((root: RootState) => root.restaurantPrinter);
  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();
  const confirm = useConfirm();
  const lastFilters = useRef<RestaurantPrinterFilters>({ page: 0, size: 20 });

  const load = async (filters: RestaurantPrinterFilters = { page: 0, size: 20 }) => {
    if (!branchId) return;
    lastFilters.current = filters;
    dispatch(setRestaurantPrinterLoading(true));
    try {
      const response = await RestaurantPrinterService.list(branchId, filters);
      dispatch(setRestaurantPrinters(response));
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setRestaurantPrinterLoading(false));
    }
  };

  const edit = async (printer: RestaurantPrinterDTO) => {
    try {
      const response = await RestaurantPrinterService.get(printer.id);
      dispatch(openRestaurantPrinterEdit(response.data));
    } catch (error) {
      handle(error);
    }
  };

  const save = async () => {
    if (!branchId || state.saving) return;
    dispatch(setRestaurantPrinterSaving(true));
    try {
      const response = state.form.id
        ? await RestaurantPrinterService.update(state.form.id, state.form)
        : await RestaurantPrinterService.create(branchId, state.form);
      show(
        response.type || 'success',
        response.title || 'Impresora guardada',
        response.message || 'La configuración se guardó correctamente.'
      );
      dispatch(closeRestaurantPrinterModal());
      await load(lastFilters.current);
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setRestaurantPrinterSaving(false));
    }
  };

  const deactivate = (printer: RestaurantPrinterDTO) => {
    confirm({
      title: 'Desactivar impresora',
      message: `¿Desea desactivar “${printer.name}”? Dejará de estar disponible para nuevas asignaciones.`,
      icon: 'warning',
      confirmText: 'Sí, desactivar',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          const response = await RestaurantPrinterService.deactivate(printer.id);
          show(
            response.type || 'success',
            response.title || 'Impresora desactivada',
            response.message || 'La impresora fue desactivada correctamente.'
          );
          await load(lastFilters.current);
        } catch (error) {
          handle(error);
        }
      },
    });
  };

  const updateField = <K extends keyof RestaurantPrinterForm>(
    field: K,
    value: RestaurantPrinterForm[K]
  ) => dispatch(updateRestaurantPrinterField({ field, value }));

  return {
    ...state,
    load,
    save,
    edit,
    deactivate,
    updateField,
    openCreate: () => dispatch(openRestaurantPrinterCreate()),
    closeModal: () => dispatch(closeRestaurantPrinterModal()),
  };
};

export default useRestaurantPrinters;
